/**
 * Keymap editor, Settings > Keyboard (W2-13 settings-keymap; DESIGN.md §4.20, §5.15, §8.1; ARCHITECTURE.md §6.3).
 *
 * Every registered command with its situation (context) and keys, grouped by category:
 *  - search by command name, alias, key label or situation, or find a command by pressing its keys (the search box
 *    stays an editable text field while it captures keys, so the app's keyboard router leaves the keys to it and
 *    runs none of their commands; Escape stops capturing, Tab leaves the field);
 *  - filters: all, changed by you, conflicts (red only), shared keys (information), without keys (UX-061: an
 *    untouched preset shows no conflicts, only keys shared between situations);
 *  - one chip per key: the same chord bound for several situations (Delete for notes and for markers) is one chip
 *    whose tooltip lists the situations; changing or removing it acts on all of them;
 *  - per key: change (record a new chord), remove (your additions are dropped; preset keys are unbound with a
 *    `remove: true` override, shown struck through with Restore); per command: add a key (several keys per command
 *    are fine), reset to the preset's keys. The overrides are shared by both presets: a removal of the other
 *    preset's key is labelled with that preset (Restore), one that matches no preset key any more says it has no
 *    effect (Forget);
 *  - conflicts coloured AND explained in words with an icon, each explanation once: red = another command in the
 *    same situation (must be resolved, always listed), shared = another command in a different situation (the
 *    situation decides; info styling, listed under the "Shared keys" filter);
 *  - JSON import / export of your shortcuts, "Reset all shortcuts", and the RPE `Hotkey.txt` import when the keymap
 *    service offers it (P1 hook: `KeymapService.importRpeHotkeys`);
 *  - problems the keymap found in your shortcuts (a reserved chord in an imported file, an unknown command) with a
 *    button to drop them.
 * Preset and Alt-free variant are the two rows above the editor (Settings rows with Reset). Your shortcuts are kept
 * in settings.keyboard.overrides through `KeymapService.setOverrides`.
 * Scale: the rows (with their conflicts, asked once per binding and keymap state, see keymapView.ts) are rebuilt
 * only when the keymap changes; searching and filtering re-render only the rows whose content changed.
 */

import {
  memo,
  useCallback,
  useId,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import type { AppServices, CommandCategory, KeyBinding } from '../../app/registry/types';
import type { Json } from '../../core/util/types';
import { Button, Icon, IconButton, Kbd, Segmented } from '../../ui';
import { downloadBlob, readText } from './files';
import { useKeymapBindings, useSettingsValue, type SettingsText } from './hooks';
import { KeyRecorder, type RecorderResult } from './KeyRecorderDialog';
import {
  addBinding,
  bindingTemplate,
  keymapOs,
  mergeImportedBindings,
  overrideIssues,
  pressedChords,
  removeBinding,
  replaceBinding,
  resetRow,
  restoreBinding,
  sameBinding,
  userBinding,
  type KeymapFilter,
  type RowBinding,
} from './keymapModel';
import { buildKeymapView, conflictCounts, filterKeymapView, type RowView } from './keymapView';
import { SearchField } from './SearchField';
import { m } from './settings.messages';
import s from './Settings.module.css';

export interface KeymapEditorProps {
  readonly services: AppServices;
  readonly text: SettingsText;
  /** The Settings page search (combined with the editor's own search). */
  readonly query?: string;
}

interface Recording {
  readonly key: number;
  readonly command: string;
  readonly args: Json | undefined;
  readonly when: string | undefined;
  readonly rb: RowBinding | null;
  /** The other situations of the same key in this row: a change moves them along (one chip, one key). */
  readonly others: readonly RowBinding[];
  readonly title: string;
}

interface Report {
  readonly ok: boolean;
  readonly title: string;
  readonly lines: readonly string[];
}

export function KeymapEditor(props: KeymapEditorProps) {
  const { services, text } = props;
  const { t, tr } = text;
  const effective = useKeymapBindings(services);
  const settings = useSettingsValue(services);
  const overridesNow = settings.keyboard.overrides;
  const os = keymapOs(services.keymap);

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<KeymapFilter>('all');
  const [capturing, setCapturing] = useState(false);
  /** Chords of the keys pressed while capturing: the key position first, then the typed character. */
  const [chordFilter, setChordFilter] = useState<readonly string[] | null>(null);
  const [recording, setRecording] = useState<Recording | null>(null);
  const [report, setReport] = useState<Report | null>(null);
  const [announcement, setAnnouncement] = useState('');
  const jsonInput = useRef<HTMLInputElement | null>(null);
  const rpeInput = useRef<HTMLInputElement | null>(null);
  const searchInput = useRef<HTMLInputElement | null>(null);
  const recordCounter = useRef(0);
  const captureHintId = useId();
  const presetNow = settings.keyboard.preset;

  const view = useMemo(
    () => buildKeymapView(services, t, tr, { effective, overrides: overridesNow, preset: presetNow }),
    [effective, overridesNow, presetNow, services, t, tr],
  );
  // Cheap: the app keymap caches its issues until it changes (every change re-renders this editor).
  const issues = overrideIssues(services.keymap);
  const pageQuery = (props.query ?? '').trim();
  const combinedQuery = `${pageQuery} ${query}`.trim();
  const rows = useMemo(
    () => filterKeymapView(view, { query: combinedQuery, filter, chord: chordFilter }),
    [view, combinedQuery, filter, chordFilter],
  );
  const changedCount = view.filter((r) => r.row.changed).length;
  const counts = conflictCounts(view);

  // Stable callbacks: the rows are memoised and re-render only when their own content changes.
  const overrides = useCallback(
    (): readonly KeyBinding[] => services.settings.value.keyboard.overrides,
    [services.settings],
  );
  const apply = useCallback(
    (next: readonly KeyBinding[], message: string): void => {
      services.keymap.setOverrides(next);
      setAnnouncement(message);
    },
    [services.keymap],
  );

  // ---- recording ----
  const startRecording = useCallback(
    (row: RowView, group: readonly RowBinding[] | null): void => {
      recordCounter.current += 1;
      const rb = group?.[0] ?? null;
      const tmpl = bindingTemplate(services.keymap.bindings(), row.row.commandId, rb?.binding.chord, row.row.args);
      setRecording({
        key: recordCounter.current,
        command: row.row.commandId,
        args: rb?.binding.args ?? row.row.args,
        when: rb?.binding.when ?? tmpl.when,
        rb,
        others: group === null ? [] : group.slice(1),
        title: row.title,
      });
    },
    [services.keymap],
  );
  const finishRecording = (result: RecorderResult | null): void => {
    const rec = recording;
    setRecording(null);
    if (result === null || rec === null) return;
    const base = overrides();
    let next =
      rec.rb === null
        ? addBinding(base, userBinding({ chord: result.chord, command: rec.command, args: rec.args, when: rec.when }))
        : replaceBinding(base, rec.rb, result.chord);
    for (const other of rec.others) next = replaceBinding(next, other, result.chord);
    apply(next, t(m.announceSaved, { chord: services.keymap.label(result.chord), command: rec.title }));
  };

  // ---- key capture in the search box ("find by keys") ----
  const stopCapture = (): void => {
    setCapturing(false);
    setChordFilter(null);
  };
  /**
   * Every key pressed in the box while capturing is looked up instead of typed or run. The box is an editable text
   * field, so the keyboard router does not route the key in its capture phase; the few keys it hands to the app
   * after a field (Esc, Enter, Ctrl+Enter, Ctrl+Z) are skipped there because this handler called preventDefault.
   * Tab (with or without Shift) is left alone: capturing never traps the keyboard.
   */
  const onSearchKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>): void => {
    if (!capturing) return;
    if (e.key === 'Tab' && !e.ctrlKey && !e.altKey && !e.metaKey) return;
    e.preventDefault();
    e.stopPropagation();
    if (e.nativeEvent.isComposing) return;
    if (e.key === 'Escape' && !e.ctrlKey && !e.altKey && !e.shiftKey && !e.metaKey) {
      stopCapture();
      return;
    }
    const chord = services.keymap.chordOf(e.nativeEvent);
    if (chord !== null) setChordFilter(pressedChords(chord, e.key));
  };

  // ---- import / export ----
  const exportJson = (): void => {
    downloadBlob(new Blob([services.keymap.exportJson()], { type: 'application/json' }), 'phiedit-keymap.json');
    setAnnouncement(t(m.exportDone));
  };
  const importJson = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file === undefined) return;
    let content: string;
    try {
      content = await readText(file);
    } catch {
      setReport({ ok: false, title: t(m.importFailedTitle), lines: [t(m.importUnreadable)] });
      return;
    }
    const before = services.settings.value.keyboard;
    const result = services.keymap.importJson(content);
    // A file with some unusable entries still imports the others (the keyboard settings changed).
    const applied = services.settings.value.keyboard !== before;
    setReport({
      ok: result.ok,
      title: t(result.ok ? m.importDoneTitle : applied ? m.importPartialTitle : m.importFailedTitle),
      lines: [...result.errors],
    });
    if (result.ok) services.ui.toast({ key: m.importDoneTitle }, { kind: 'success' });
  };
  const rpeAvailable = typeof services.keymap.importRpeHotkeys === 'function';
  const importRpe = async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file === undefined || services.keymap.importRpeHotkeys === undefined) return;
    let content: string;
    try {
      content = await readText(file);
    } catch {
      setReport({ ok: false, title: t(m.importFailedTitle), lines: [t(m.importUnreadable)] });
      return;
    }
    const result = services.keymap.importRpeHotkeys(content);
    const unmapped =
      result.unmapped.length === 0
        ? []
        : [t(m.rpeUnmapped, { count: result.unmapped.length, names: result.unmapped.join(', ') })];
    if (result.bindings.length === 0) {
      setReport({ ok: false, title: t(m.rpeImportFailedTitle), lines: [t(m.rpeImportNone), ...unmapped] });
      return;
    }
    // RPE's keys replace the keys of the functions they name; other commands keep theirs.
    services.keymap.setOverrides(mergeImportedBindings(overrides(), services.keymap.bindings(), result.bindings));
    setReport({
      ok: result.unmapped.length === 0,
      title: t(m.rpeImported, { count: result.bindings.length }),
      lines: unmapped,
    });
    setAnnouncement(t(m.rpeImported, { count: result.bindings.length }));
  };
  const resetAll = async (): Promise<void> => {
    const ok = await services.ui.confirm({
      title: { key: m.resetShortcutsTitle },
      body: { key: m.resetShortcutsBody, params: { count: overrides().length } },
      confirm: { key: m.resetShortcutsConfirm },
      danger: true,
    });
    if (ok) apply([], t(m.resetShortcutsDone));
  };

  // ---- render ----
  const groups: Array<{ readonly category: CommandCategory | null; readonly label: string; readonly rows: RowView[] }> =
    [];
  for (const r of rows) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.category === r.categoryId) last.rows.push(r);
    else groups.push({ category: r.categoryId, label: r.category, rows: [r] });
  }
  const capturedChord = chordFilter?.[0];
  const searchValue = capturing ? (capturedChord === undefined ? '' : services.keymap.label(capturedChord)) : query;

  return (
    <div className={s.keymap}>
      <div className={s.blockHeader}>
        <div>
          <h3 className={s.blockTitle}>{t(m.keymapTitle)}</h3>
          <p className={s.blockDesc}>{t(m.kmDesc)}</p>
        </div>
      </div>
      {issues.length > 0 && (
        <div className={s.kmIssues} role="alert">
          <Icon name="TriangleAlert" size={16} />
          <div>
            <p className={s.kmIssuesTitle}>{t(m.issuesTitle, { count: issues.length })}</p>
            <ul className={s.kmIssueList}>
              {issues.map((issue, i) => (
                <li key={`${issue.code}:${String(i)}`}>{tr(issue.text)}</li>
              ))}
            </ul>
            {issues.some((i) => i.binding !== null) && (
              <Button
                size="sm"
                icon="Trash"
                onClick={() => {
                  const bad = issues.map((i) => i.binding).filter((b): b is KeyBinding => b !== null);
                  apply(
                    overrides().filter(
                      (o) => !bad.some((b) => sameBinding(b, o) && (b.remove === true) === (o.remove === true)),
                    ),
                    t(m.issuesDropped),
                  );
                }}
              >
                {t(m.issuesDrop)}
              </Button>
            )}
          </div>
        </div>
      )}

      <div className={s.kmToolbar}>
        <SearchField
          ref={searchInput}
          className={s.kmSearch}
          value={searchValue}
          onChange={(v) => {
            // While capturing, typed text never lands (keydown prevented); emptying the box (clear button, cut)
            // stops capturing, anything else (a paste from the context menu) is ignored.
            if (capturing) {
              if (v === '') stopCapture();
              return;
            }
            setQuery(v);
          }}
          capturing={capturing}
          describedBy={capturing ? captureHintId : undefined}
          onKeyDown={onSearchKeyDown}
          label={t(capturing ? m.kmSearchCaptureLabel : m.kmSearchLabel)}
          placeholder={t(capturing ? m.kmSearchCapturePlaceholder : m.kmSearchPlaceholder)}
          trailing={
            <IconButton
              icon="Keyboard"
              size="sm"
              label={{ key: m.kmFindByKeys }}
              pressed={capturing}
              onClick={() => {
                const next = !capturing;
                setCapturing(next);
                setChordFilter(null);
                // The keys go to the box: focus it (the toolbar button does not keep focus).
                if (next) searchInput.current?.focus();
              }}
            />
          }
        />
        {capturing && (
          <p id={captureHintId} className={s.kmCaptureHint}>
            {t(m.kmCapturing)}
          </p>
        )}
        <Segmented<KeymapFilter>
          ariaLabel={t(m.kmFilterLabel)}
          size="sm"
          value={filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: t(m.kmFilterAll) },
            { value: 'changed', label: t(m.kmFilterChanged, { count: changedCount }) },
            { value: 'conflicts', label: t(m.kmFilterConflicts, { count: counts.conflicts }) },
            { value: 'shared', label: t(m.kmFilterShared, { count: counts.shared }) },
            { value: 'unbound', label: t(m.kmFilterUnbound) },
          ]}
        />
        <div className={s.kmActions}>
          <Button
            size="sm"
            icon="FileUp"
            onClick={() => {
              jsonInput.current?.click();
            }}
          >
            {t(m.kmImport)}
          </Button>
          <Button size="sm" icon="FileDown" onClick={exportJson}>
            {t(m.kmExport)}
          </Button>
          {rpeAvailable && (
            <Button
              size="sm"
              icon="FileInput"
              onClick={() => {
                rpeInput.current?.click();
              }}
            >
              {t(m.kmImportRpe)}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            icon="RotateCcw"
            disabledReason={overridesNow.length === 0 ? { key: m.kmNothingToReset } : null}
            onClick={() => {
              void resetAll();
            }}
          >
            {t(m.kmResetAll)}
          </Button>
          <input
            ref={jsonInput}
            type="file"
            accept=".json,application/json"
            hidden
            aria-label={t(m.kmImport)}
            onChange={(e) => {
              void importJson(e);
            }}
          />
          <input
            ref={rpeInput}
            type="file"
            accept=".txt,text/plain"
            hidden
            aria-label={t(m.kmImportRpe)}
            onChange={(e) => {
              void importRpe(e);
            }}
          />
        </div>
      </div>

      {report !== null && (
        <div className={s.kmReport} data-ok={report.ok ? 'true' : 'false'} role="status">
          <Icon name={report.ok ? 'CircleCheck' : 'TriangleAlert'} size={16} />
          <div className={s.kmReportBody}>
            <p className={s.kmReportTitle}>{report.title}</p>
            {report.lines.length > 0 && (
              <ul className={s.kmIssueList}>
                {report.lines.map((line, i) => (
                  <li key={String(i)}>{line}</li>
                ))}
              </ul>
            )}
          </div>
          <IconButton
            icon="X"
            size="sm"
            label={{ key: m.dismiss }}
            onClick={() => {
              setReport(null);
            }}
          />
        </div>
      )}

      <p className={s.kmLegend}>
        <span className={s.kmLegendItem} data-level="red">
          <Icon name="TriangleAlert" size={14} />
          {t(m.legendRed)}
        </span>
        <span className={s.kmLegendItem} data-level="yellow">
          <Icon name="Info" size={14} />
          {t(m.legendYellow)}
        </span>
        <span className={s.kmLegendItem}>
          <Icon name="UserPen" size={14} />
          {t(m.legendYours)}
        </span>
        {os === 'mac' && <span className={s.kmLegendItem}>{t(m.legendMac)}</span>}
      </p>

      {pageQuery !== '' && (
        <p className={s.kmPageFilter}>
          <Icon name="ListFilter" size={14} />
          {t(m.kmPageFilter, { query: pageQuery })}
        </p>
      )}
      <p className={s.kmCount} role="status" aria-live="polite">
        {t(m.kmCount, { count: rows.length, total: view.length })}
      </p>
      <p className="visually-hidden" aria-live="polite">
        {announcement}
      </p>

      {rows.length === 0 ? (
        <p className={s.kmEmpty}>
          {chordFilter !== null
            ? t(m.kmNoneForKeys)
            : combinedQuery !== ''
              ? t(m.kmNoneForQuery, { query: combinedQuery })
              : t(m.kmNone)}
        </p>
      ) : (
        <table className={s.kmTable}>
          <thead>
            <tr>
              <th scope="col">{t(m.kmColCommand)}</th>
              <th scope="col">{t(m.kmColContext)}</th>
              <th scope="col">{t(m.kmColKeys)}</th>
              <th scope="col">
                <span className="visually-hidden">{t(m.kmColActions)}</span>
              </th>
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={`${g.category ?? 'unknown'}:${g.rows[0]?.row.key ?? ''}`} className={s.kmGroup}>
              <tr>
                <th scope="colgroup" colSpan={4} className={s.kmCategory}>
                  {g.label}
                </th>
              </tr>
              {g.rows.map((r) => (
                <KeymapRowView
                  key={r.row.key}
                  text={text}
                  row={r}
                  onRecord={startRecording}
                  onApply={apply}
                  overrides={overrides}
                  showShared={filter === 'shared'}
                />
              ))}
            </tbody>
          ))}
        </table>
      )}

      {recording !== null && (
        <KeyRecorder
          key={recording.key}
          services={services}
          open
          command={recording.command}
          args={recording.args}
          when={recording.when}
          current={recording.rb?.binding ?? null}
          onClose={finishRecording}
        />
      )}
    </div>
  );
}

interface RowProps {
  readonly text: SettingsText;
  readonly row: RowView;
  /** List the shared-key explanations too (the "Shared keys" filter); conflicts are always listed. */
  readonly showShared: boolean;
  onRecord(row: RowView, group: readonly RowBinding[] | null): void;
  onApply(next: readonly KeyBinding[], message: string): void;
  overrides(): readonly KeyBinding[];
}

/** One command row (memoised: the row view keeps its identity until the keymap changes). */
const KeymapRowView = memo(function KeymapRowView(props: RowProps) {
  const { row, text } = props;
  const { t } = text;
  const conflicts = row.conflictLines.filter((c) => c.level === 'red' || props.showShared);
  return (
    <>
      <tr
        className={s.kmRow}
        data-conflict={row.worstConflict ?? undefined}
        data-changed={row.row.changed ? 'true' : undefined}
      >
        <td className={s.kmCommand}>
          <span className={s.kmTitle}>{row.title}</span>
          <span className={s.kmId}>{row.row.commandId}</span>
          {row.row.changed && <span className={s.changed}>{t(m.changed)}</span>}
          {row.row.command === undefined && <span className={s.kmUnknown}>{t(m.kmUnknownCommand)}</span>}
        </td>
        <td className={s.kmContext}>{row.context}</td>
        <td className={s.kmKeys}>
          {row.bindings.length === 0 && row.removed.length === 0 && <span className={s.kmNoKeys}>{t(m.kmNoKeys)}</span>}
          {row.chips.map((c) => {
            const level = c.level ?? undefined;
            const shared = c.level === 'yellow' ? ` ${t(m.conflictYellowShort)}` : '';
            return (
              <span
                key={`${c.chord}|${c.items.map((b) => b.rb.binding.when ?? '').join('|')}`}
                className={s.chip}
                data-conflict={level}
                data-source={c.user ? 'user' : 'preset'}
                data-situations={c.items.length > 1 ? String(c.items.length) : undefined}
              >
                <button
                  type="button"
                  className={s.chipMain}
                  aria-label={t(m.chipChange, { chord: c.label, command: row.title })}
                  title={`${t(m.chipChange, { chord: c.label, command: row.title })} (${c.context})${shared}`}
                  onClick={() => {
                    props.onRecord(
                      row,
                      c.items.map((b) => b.rb),
                    );
                  }}
                >
                  <Kbd chord={c.chord} />
                  {c.user && <Icon name="UserPen" size={12} label={{ key: m.legendYours }} />}
                  {level === 'red' && <Icon name="TriangleAlert" size={12} label={{ key: m.conflictRedShort }} />}
                  {level === 'yellow' && <Icon name="Info" size={12} label={{ key: m.conflictYellowShort }} />}
                </button>
                <button
                  type="button"
                  className={s.chipRemove}
                  aria-label={t(m.chipRemove, { chord: c.label, command: row.title })}
                  title={t(m.chipRemove, { chord: c.label, command: row.title })}
                  onClick={() => {
                    let next = props.overrides();
                    for (const b of c.items) next = removeBinding(next, b.rb);
                    props.onApply(next, t(m.announceRemoved, { chord: c.label, command: row.title }));
                  }}
                >
                  <Icon name="X" size={12} />
                </button>
              </span>
            );
          })}
          {row.removed.map((r) => (
            <span key={`removed:${r.binding.chord}|${r.binding.when ?? ''}`} className={s.chip} data-removed="true">
              <span className={s.chipRemoved}>
                <Kbd chord={r.binding.chord} />
                <span className="visually-hidden">{t(m.chipRemovedSr)}</span>
              </span>
              <button
                type="button"
                className={s.chipRemove}
                aria-label={t(m.chipRestore, { chord: r.label, command: row.title })}
                title={t(m.chipRestore, { chord: r.label, command: row.title })}
                onClick={() => {
                  props.onApply(
                    restoreBinding(props.overrides(), r.binding),
                    t(m.announceRestored, { chord: r.label, command: row.title }),
                  );
                }}
              >
                <Icon name="Undo2" size={12} />
              </button>
            </span>
          ))}
          {row.removedElsewhere.map((r) => {
            const preset = r.preset === null ? null : t(r.preset === 'rpe' ? m.ctxPresetRpe : m.ctxPresetDefault);
            const hint =
              preset === null
                ? t(m.chipRemovedStaleHint, { chord: r.label })
                : t(m.chipRemovedInHint, { chord: r.label, preset });
            const action =
              preset === null
                ? t(m.chipForget, { chord: r.label, command: row.title })
                : t(m.chipRestoreIn, { chord: r.label, command: row.title, preset });
            return (
              <span
                key={`elsewhere:${r.binding.chord}|${r.binding.when ?? ''}`}
                className={s.chip}
                data-removed="elsewhere"
                title={hint}
              >
                <span className={s.chipRemoved}>
                  <Kbd chord={r.binding.chord} />
                  <span className="visually-hidden">{t(m.chipRemovedSr)}</span>
                </span>
                <span className={s.chipTag}>
                  {preset === null ? t(m.chipRemovedStale) : t(m.chipRemovedIn, { preset })}
                </span>
                <button
                  type="button"
                  className={s.chipRemove}
                  aria-label={action}
                  title={action}
                  onClick={() => {
                    props.onApply(
                      restoreBinding(props.overrides(), r.binding),
                      preset === null
                        ? t(m.announceForgot, { chord: r.label, command: row.title })
                        : t(m.announceRestored, { chord: r.label, command: row.title }),
                    );
                  }}
                >
                  <Icon name={preset === null ? 'Trash' : 'Undo2'} size={12} />
                </button>
              </span>
            );
          })}
        </td>
        <td className={s.kmRowActions}>
          {row.row.command !== undefined && (
            <IconButton
              icon="Plus"
              size="sm"
              label={{ key: m.addShortcut, params: { command: row.title } }}
              onClick={() => {
                props.onRecord(row, null);
              }}
            />
          )}
          {row.row.changed && (
            <IconButton
              icon="RotateCcw"
              size="sm"
              label={{ key: m.resetCommand, params: { command: row.title } }}
              onClick={() => {
                props.onApply(resetRow(props.overrides(), row.row), t(m.announceReset, { command: row.title }));
              }}
            />
          )}
        </td>
      </tr>
      {conflicts.length > 0 && (
        <tr className={s.kmConflictRow}>
          <td colSpan={4}>
            <ul className={s.kmConflicts}>
              {conflicts.map((c, i) => (
                <li key={`${c.other}:${String(i)}`} data-level={c.level}>
                  <Icon name={c.level === 'red' ? 'TriangleAlert' : 'Info'} size={14} />
                  <span>
                    {c.level === 'red'
                      ? t(m.conflictRed, { chord: c.labels, other: c.other, context: c.context })
                      : t(m.conflictYellow, { chord: c.labels, other: c.other, context: c.context })}
                  </span>
                  {c.level === 'red' && <span className={s.conflictFix}>{t(m.conflictFixHint)}</span>}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
});
